import * as React from "react";
import "./LookupCtl.scss";
import { ListItem } from "@testout/testout-commerce/models/ListItem";

interface ILookupProps {
    value?: string;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
    onChange?: (value: string, data?: {[key: string]: string|undefined}) => void;
    onSearch: (value: string, exact?: boolean) => Promise<ListItem[]>;
    onSelect?: (value: string) => void;
    style?: React.CSSProperties;
    hideItemValue?: boolean;
    minCharsForSearch?: number;
    alwaysOpen?: boolean;
    initialSearchValue?: string;
}

interface ILookupState {
    isOpen: boolean;
    inProgress: boolean;
    selectedItem: ListItem;
    items: ListItem[];
    focusIndex: number;
    searchValue: string;
    error: string;
}

export default class LookupCtl extends React.PureComponent<ILookupProps, ILookupState> {
    constructor(props: ILookupProps) {
        super(props);
        this.state = {
            isOpen: this.props.alwaysOpen ?? false,
            inProgress: false,
            selectedItem: { value: this.props.value || "", name: "" },
            items: [],
            focusIndex: -1,
            searchValue: "",
            error: "",
        };
    }

    _controlRef = React.createRef<HTMLDivElement>();
    _valueRef = React.createRef<HTMLDivElement>();
    _scrollerRef = React.createRef<HTMLDivElement>();
    _itemRef = React.createRef<HTMLLIElement>();
    _searchRef = React.createRef<HTMLInputElement>();
    _needFocus = false;

    onSelectedItemClick = () => {
        if (this.props.disabled) return;
        this._needFocus = true;
        if (this.state.isOpen)
            this.setState({ isOpen: this.props.alwaysOpen ?? false, inProgress: false, focusIndex: -1, searchValue: "", items: [], error: "" });
        else
            this.setState({ isOpen: true });
    }

    onSelectedItemKeyDown = (e: React.KeyboardEvent) => {
        if (this.props.disabled) return;
        if (e.key === "Enter") {
            this._needFocus = true;
            if (this.state.isOpen) {
                if (this.state.focusIndex >= 0)
                    this.onItemClick(this.state.focusIndex);
                else
                    this.setState({ isOpen: this.props.alwaysOpen ?? false, inProgress: false, focusIndex: -1, searchValue: "", items: [], error: "" });
            } else
                this.setState({ isOpen: true });
            e.preventDefault();
        } else if (e.key === "Escape") {
            if (this.state.isOpen) {
                this._needFocus = true;
                this.setState({ isOpen: this.props.alwaysOpen ?? false, inProgress: false, focusIndex: -1, searchValue: "", items: [], error: "" });
            }
            e.preventDefault();
        } else if (e.key === "ArrowDown") {
            console.log(e.key);
            if (this.state.focusIndex < this.state.items.length - 1) {
                this.setState({ focusIndex: this.state.focusIndex + 1 });
                if (!this.state.isOpen)
                    this.onItemClick(this.state.focusIndex + 1);
            }
            e.preventDefault();
        } else if (e.key === "ArrowUp") {
            if (this.state.focusIndex > 0) {
                this.setState({ focusIndex: this.state.focusIndex - 1 });
                if (!this.state.isOpen)
                    this.onItemClick(this.state.focusIndex - 1);
            }
            e.preventDefault();
        }
    }

    onSearchValueChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        this.updateList(e.target.value);
    }

    private _debounceTimer: number | undefined = undefined;
    async updateList(value: string) {
        this.setState({ searchValue: value });
            if (this._debounceTimer)
                window.clearTimeout(this._debounceTimer);
            this._debounceTimer = window.setTimeout(async () => {
                this._debounceTimer = undefined;
                if (value.length >= (this.props.minCharsForSearch ?? 3)) {
                    this.setState({ inProgress: true, focusIndex: -1 });

                    try {
                        const items = await this.props.onSearch(value);
                        this.setState({ inProgress: false, items: items });
                    } catch (error: any) {
                        this.setState({ inProgress: false, items: [], error: `Search failed. ${error.message}` });
                    }
                } else
                    this.setState({ items: [] });
            }, 300);
    }

    onItemClick = (itemIndex: number) => {
        this._needFocus = true;
        const item = this.state.items[itemIndex];
        this.setState({ isOpen: this.props.alwaysOpen ?? false, inProgress: false, focusIndex: -1, searchValue: "", items: [], error: "" });
        if (this.props.onChange) this.props.onChange(item.value || "", item.data);
        if (this.props.onSelect) this.props.onSelect(item.value || "");
    }

    onItemClearClick = () => {
        this._needFocus = true;
        this.setState({ isOpen: this.props.alwaysOpen ?? false, inProgress: false, focusIndex: -1, searchValue: "", items: [], error: "" });
        if (this.props.onChange) this.props.onChange("", undefined);
        if (this.props.initialSearchValue) this.updateList(this.props.initialSearchValue);
    }

    onDocumentFocusIn = (e: FocusEvent) => {
        const lookup = this._controlRef.current;
        if (lookup && !lookup.contains(e.target as Node))
            this.setState({ isOpen: this.props.alwaysOpen ?? false, inProgress: false, focusIndex: -1, searchValue: "", items: [], error: "" });
    }

    ensureItemVisible() {
        if (this.state.isOpen && this.state.focusIndex >= 0 && this._scrollerRef.current && this._itemRef.current) {
            const liItem = this._itemRef.current as HTMLLIElement;
            const divScroller = this._scrollerRef.current as HTMLDivElement;
            if (liItem.offsetTop < divScroller.offsetTop + divScroller.scrollTop)
                divScroller.scrollTop = liItem.offsetTop - divScroller.offsetTop;
            if (liItem.offsetTop + liItem.offsetHeight > divScroller.offsetTop + divScroller.scrollTop + divScroller.clientHeight)
                divScroller.scrollTop = liItem.offsetTop + liItem.offsetHeight - (divScroller.offsetTop + divScroller.clientHeight);
        }
    }

    async fetchSelectedItem() {
        if (this.props.value) {
            this.setState({ selectedItem: { value: this.props.value, name: "Loading..." } });

            try {
                const item = await this.props.onSearch(this.props.value ?? "", true);
                this.setState({ selectedItem: item?.[0] ?? { value: this.props.value ?? "", name: this.props.value } });
            } catch (error: any) {
                this.setState({ selectedItem: { value: this.props.value, name: `? ${error.message}` } });
            }
        }
        else
            this.setState({ selectedItem: { value: "", name: "" } });
    }

    setFocus() {
        this._needFocus = false;
        const inpSearch = this._searchRef.current as HTMLInputElement;
        const divValue = this._valueRef.current as HTMLDivElement;
        if (this.state.isOpen) {

            inpSearch.focus();
        }
        else {
            divValue.focus();
        }
    }

    componentDidMount() {
        document.addEventListener("focusin", this.onDocumentFocusIn);
        this.ensureItemVisible();
        this.fetchSelectedItem();
        if (this.props.alwaysOpen) {
            this.setFocus();
            if (this.props.initialSearchValue)
                this.updateList(this.props.initialSearchValue);
        }
    }

    componentDidUpdate(props: ILookupProps) {
        this.ensureItemVisible();
        if (props.value !== this.props.value)
            this.fetchSelectedItem();
        if (props.initialSearchValue !== this.props.initialSearchValue && this.props.initialSearchValue)
            this.updateList(this.props.initialSearchValue);
        if (this._needFocus)
            this.setFocus();
    }

    componentWillUnmount() {
        document.removeEventListener("focusin", this.onDocumentFocusIn);
    }

    render() {
        const { selectedItem, items, focusIndex, isOpen, inProgress, searchValue, error } = this.state;
        const { hideItemValue, style, disabled, placeholder, minCharsForSearch } = this.props;
        return <div className={"lookup" + (this.props.className ? " " + this.props.className : "")}
            style={style} ref={this._controlRef}>
            <div className={"value" + (selectedItem.value ? "" : " empty") + (disabled ? " disabled" : "")}
                ref={this._valueRef}
                tabIndex={disabled ? undefined : 0}
                onClick={this.onSelectedItemClick}
                onKeyDown={this.onSelectedItemKeyDown}>
                {selectedItem.value ? selectedItem.name : placeholder}
                {selectedItem.value &&
                    selectedItem.value !== selectedItem.name &&
                    !hideItemValue &&
                    <span className={"span" + (disabled ? " disabled" : "")}>{selectedItem.value}</span>}
            </div>
            {isOpen &&
                <div className="drop-down">
                    <div className="search">
                        <div>
                            <input type="text" ref={this._searchRef}
                                placeholder="Search"
                                value={searchValue}
                                onChange={this.onSearchValueChange}
                                onKeyDown={this.onSelectedItemKeyDown} />
                            <button title="Search"></button>
                        </div>
                        {selectedItem.value && <button className="icon" title="Remove" onClick={() => this.onItemClearClick()}>&#xe5cd;</button>}
                    </div>
                    <div className="scroller" ref={this._scrollerRef}>
                        <ul>
                            {!searchValue
                                ? <li key="_msg">Type to search</li>
                                : searchValue.length < (minCharsForSearch ?? 3)
                                    ? <li key="_msg">No results, type at least {minCharsForSearch ?? 3} characters to start search</li>
                                    : inProgress
                                        ? <li key="_msg">Searching...</li>
                                        : error
                                            ? <li key="_msg" className="error">{error}</li>
                                            : items.length === 0
                                                ? <li key="_msg">No results</li>
                                                : <li key="_msg" className="results">Results</li>
                            }
                            {this.state.items.map((item: ListItem, i) =>
                                <li key={i} ref={i === focusIndex ? this._itemRef : undefined}
                                    className={i === focusIndex ? "focus" : undefined}
                                    onClick={() => this.onItemClick(i)}>
                                    {item.name}
                                    {item.value && item.value !== item.name && !hideItemValue &&
                                        <span>{item.value}</span>}
                                    {item.description &&
                                        <div>{item.description}</div>}
                                </li>
                            )}
                        </ul>
                    </div>
                </div>}
        </div>;
    }
}